import { useMutation, useQueryClient } from '@tanstack/react-query'
import irisApi from '../lib/api'
import type { UploadSession, Shortfall } from '../types'

interface FinaliseParams {
  uploaderEmail?: string
  uploaderName?: string
  attributeValues?: Record<string, unknown>
}

export class ShortfallError extends Error {
  shortfalls: Shortfall[]

  constructor(shortfalls: Shortfall[]) {
    super('Not all required slots are filled')
    this.name = 'ShortfallError'
    this.shortfalls = shortfalls
  }
}

function simulateDemoFinalise(
  queryClient: ReturnType<typeof useQueryClient>,
  token: string,
): Promise<UploadSession> {
  return new Promise((resolve, reject) => {
    const session = queryClient.getQueryData<UploadSession>(['upload-session', token])
    if (!session) return reject(new Error('No session'))

    const shortfalls: Shortfall[] = []
    for (const slot of session.slots) {
      if (slot.min > 0 && slot.uploads.length < slot.min) {
        shortfalls.push({
          slot_uuid: slot.id,
          requirement_key: slot.requirement_key,
          label: slot.label,
          have: slot.uploads.length,
          need: slot.min,
        })
      }
    }

    if (shortfalls.length > 0) {
      return reject(new ShortfallError(shortfalls))
    }

    setTimeout(() => {
      resolve({
        ...session,
        state: 'finalised',
        finalised_at: new Date().toISOString(),
      })
    }, 600)
  })
}

export function useFinalise(token: string, isDemo = false) {
  const queryClient = useQueryClient()

  return useMutation<UploadSession, Error, FinaliseParams>({
    mutationFn: async ({ uploaderEmail, uploaderName, attributeValues }) => {
      if (isDemo) return simulateDemoFinalise(queryClient, token)

      try {
        const res = await irisApi.post(`/${token}/finalise/`, {
          uploader_email: uploaderEmail || undefined,
          uploader_name: uploaderName || undefined,
          attribute_values: attributeValues,
        })
        return res.data
      } catch (err: unknown) {
        if (
          err &&
          typeof err === 'object' &&
          'response' in err &&
          (
            err as {
              response?: {
                status?: number
                data?: { result?: { shortfalls?: Shortfall[] } }
              }
            }
          ).response?.status === 422
        ) {
          const shortfalls =
            (
              err as {
                response: { data: { result?: { shortfalls?: Shortfall[] } } }
              }
            ).response.data.result?.shortfalls || []
          throw new ShortfallError(shortfalls)
        }
        throw err
      }
    },
    onSuccess: () => {
      if (!isDemo) {
        queryClient.invalidateQueries({ queryKey: ['upload-session', token] })
      }
    },
  })
}
